import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Filter, Plus, GitMerge, Eye, ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DateSectionHeader from '@/components/admin/DateSectionHeader';
import { groupItemsByDateSection } from '@/utils/dateSections';
import { formatDateTimeMdY } from '@/utils/dateFormat';
import AdminPageHeader from '@/components/admin/AdminPageHeader';
import AdminKpiGrid, { AdminKpiTile } from '@/components/admin/AdminKpiGrid';
import AdminFilterBar from '@/components/admin/AdminFilterBar';
import AdminRecordCard from '@/components/admin/AdminRecordCard';
import useAdminListLoupe, { assignLoupeIndexes } from '@/components/admin/useAdminListLoupe';
import { adminDialogPanel, adminOverlay } from '@/components/admin/adminUi';
import {
  CONVERT_LEAD_TYPE_LABELS,
  ConvertLeadModal,
  ConvertLeadPicker,
  CreateDealModal,
  PAYMENT_TYPES,
} from '@/components/admin/DealWorkflowModals';
import { getDealStatusLabel, getLeadTypeLabel } from '@/constants/adminVocabulary';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const DEAL_STATUSES = [
  { value: 'approval_pending', label: getDealStatusLabel('approval_pending'), color: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  { value: 'approved', label: getDealStatusLabel('approved'), color: 'bg-green-500/15 text-green-400 border-green-500/30' },
  { value: 'ready_to_close', label: getDealStatusLabel('ready_to_close'), color: 'bg-purple-500/15 text-purple-400 border-purple-500/30' },
  { value: 'funded', label: getDealStatusLabel('funded'), color: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30' },
  { value: 'delivered', label: getDealStatusLabel('delivered'), color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
  { value: 'filed_taxes', label: getDealStatusLabel('filed_taxes'), color: 'bg-teal-500/15 text-teal-400 border-teal-500/30' },
  { value: 'archived', label: getDealStatusLabel('archived'), color: 'bg-gray-500/15 text-gray-400 border-gray-500/30' },
];

const LEGACY_PENDING_APPROVAL_STATUSES = ['credit_pending'];
const PENDING_APPROVAL_STATUSES = ['approval_pending', ...LEGACY_PENDING_APPROVAL_STATUSES];

const sortDealsNewestFirst = (list) => [...list].sort((a, b) => {
  const aTime = new Date(a.created_at || 0).getTime();
  const bTime = new Date(b.created_at || 0).getTime();
  return bTime - aTime;
});

const isPendingApprovalStatus = (status) => PENDING_APPROVAL_STATUSES.includes(status);

const getStatusBadge = (status) => {
  const normalized = LEGACY_PENDING_APPROVAL_STATUSES.includes(status) ? 'approval_pending' : status;
  const s = DEAL_STATUSES.find((d) => d.value === normalized) || {
    label: getDealStatusLabel(status),
    color: 'bg-white/10 text-white/50 border-white/20',
  };
  return (
    <span className={`px-2.5 py-1 text-[11px] font-['Manrope'] font-medium border ${s.color}`}>
      {s.label}
    </span>
  );
};

const formatDate = (dateString) => formatDateTimeMdY(dateString);

export default function DealsPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const listLoupe = useAdminListLoupe(`${searchTerm}|${statusFilter}`);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);

  const fetchDeals = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/deals`, { headers });
      setDeals(sortDealsNewestFirst(res.data || []));
    } catch (err) {
      toast.error('Failed to load deals');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  // Filtered deals (preserve newest-first order)
  const filtered = deals.filter((d) => {
    if (statusFilter !== 'all') {
      if (statusFilter === 'approval_pending') {
        if (!isPendingApprovalStatus(d.status)) return false;
      } else if (d.status !== statusFilter) {
        return false;
      }
    }
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      const vehicleName = [
        d.vehicle_name,
        d.vehicle_year,
        d.vehicle_make,
        d.vehicle_model,
        d.vehicle_trim,
      ].filter(Boolean).join(' ').toLowerCase();
      return (
        d.customer_name?.toLowerCase().includes(q) ||
        vehicleName.includes(q)
      );
    }
    return true;
  });

  const groupedDeals = useMemo(
    () => groupItemsByDateSection(filtered, (deal) => deal.created_at),
    [filtered],
  );
  const dealLoupeIndexes = assignLoupeIndexes(groupedDeals);

  const toggleStatusFilter = (value) => {
    setStatusFilter((current) => (value === 'all' || current === value ? 'all' : value));
  };

  const stats = [
    { key: 'all', label: 'Total Deals', value: deals.length, color: 'text-white', testId: 'stat-total-deals' },
    { key: 'approval_pending', label: 'Pending', value: deals.filter((d) => isPendingApprovalStatus(d.status)).length, color: 'text-amber-400', testId: 'stat-pending' },
    { key: 'ready_to_close', label: 'Ready to Close', value: deals.filter((d) => d.status === 'ready_to_close').length, color: 'text-purple-400', testId: 'stat-ready-to-close' },
    { key: 'delivered', label: 'Delivered', value: deals.filter((d) => d.status === 'delivered').length, color: 'text-emerald-400', testId: 'stat-delivered' },
  ];

  return (
    <div className="p-4 sm:p-6 max-w-[1400px] mx-auto" data-testid="deals-page">
      <AdminPageHeader
        title="Deals Hub"
        subtitle="Create, track, and manage every sale"
        actions={
          <>
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              data-testid="create-deal-btn"
              className="min-h-11 flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-['Manrope'] font-semibold"
            >
              <Plus className="w-4 h-4" />
              Create Deal
            </button>
            <button
              type="button"
              onClick={() => setShowConvertModal(true)}
              data-testid="convert-lead-btn"
              className="min-h-11 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold"
            >
              <Plus className="w-4 h-4" />
              Convert Lead
            </button>
            <button
              type="button"
              onClick={() => setShowMergeModal(true)}
              data-testid="merge-deals-btn"
              className="min-h-11 flex items-center justify-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-white/15 text-white text-sm font-['Manrope'] font-medium border border-white/10"
            >
              <GitMerge className="w-4 h-4" />
              Merge Trade-in Lead
            </button>
          </>
        }
      />

      <AdminKpiGrid>
        {stats.map((s) => (
          <AdminKpiTile
            key={s.key}
            label={s.label}
            value={s.value}
            color={s.color}
            testId={s.testId}
            active={s.key === 'all' ? statusFilter === 'all' : statusFilter === s.key}
            onClick={() => toggleStatusFilter(s.key)}
          />
        ))}
      </AdminKpiGrid>

      <AdminFilterBar
        search={searchTerm}
        onSearch={setSearchTerm}
        searchPlaceholder="Search by customer or vehicle..."
        searchTestId="deals-search-input"
      >
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 pointer-events-none" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="deals-status-filter"
            className="w-full lg:w-auto bg-white/5 border border-white/10 text-white pl-10 pr-8 py-3 font-['Manrope'] focus:outline-none focus:border-blue-500/50 appearance-none cursor-pointer [&>option]:bg-gray-900 [&>option]:text-white"
          >
            <option value="all">All Statuses</option>
            {DEAL_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
      </AdminFilterBar>

      {/* Table */}
      <div className="admin-list-card bg-white/5 border border-white/10 overflow-hidden" data-testid="deals-table">
        {loading ? (
          <div className="p-12 text-center text-white/40 font-['Manrope']">Loading deals...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-white/40 font-['Manrope']">
            {deals.length === 0 ? (
              <>
                <p>No deals yet. Create a deal or convert a lead to get started.</p>
                <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(true)}
                    data-testid="deals-empty-create-btn"
                    className="min-h-11 flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-['Manrope'] font-semibold"
                  >
                    <Plus className="w-4 h-4" />
                    Create Deal
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowConvertModal(true)}
                    data-testid="deals-empty-convert-btn"
                    className="min-h-11 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold"
                  >
                    <Plus className="w-4 h-4" />
                    Convert Lead
                  </button>
                </div>
              </>
            ) : 'No deals match your filters.'}
          </div>
        ) : (
          <>
          <div className="lg:hidden space-y-3 p-3">
            {groupedDeals.map((section) => (
              <React.Fragment key={section.key}>
                <DateSectionHeader variant="list" label={section.label} count={section.items.length} compact />
                {section.items.map((deal) => (
                  <AdminRecordCard
                    key={deal.id}
                    title={deal.customer_name || '—'}
                    subtitle={
                      deal.vehicle_name
                      || [deal.vehicle_year, deal.vehicle_make, deal.vehicle_model, deal.vehicle_trim].filter(Boolean).join(' ')
                      || '—'
                    }
                    badge={getStatusBadge(deal.status)}
                    fields={[
                      { label: 'Price', value: deal.sale_price ? `$${deal.sale_price.toLocaleString()}` : '—' },
                      { label: 'Created', value: formatDate(deal.created_at) },
                    ]}
                    onClick={() => navigate(`/admin/deals/${deal.id}`)}
                    primary={
                      <button
                        type="button"
                        onClick={() => navigate(`/admin/deals/${deal.id}`)}
                        className="w-full min-h-11 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold"
                      >
                        Open
                      </button>
                    }
                  />
                ))}
              </React.Fragment>
            ))}
          </div>
          <div className="admin-list-scroll hidden lg:block overflow-x-auto">
            <div className="admin-list-cols-deals bg-white/5 border-b border-white/10">
              {['Customer', 'Vehicle', 'Status', 'Sale Price', 'Created', 'Actions'].map((h) => (
                <div key={h} className="px-4 py-3 text-left text-[11px] font-['Manrope'] font-semibold text-white/60 uppercase tracking-wider">
                  {h}
                </div>
              ))}
            </div>
            <div
              className={`admin-list-loupe ${listLoupe.active ? 'is-loupe' : ''}`}
              onMouseLeave={listLoupe.clear}
            >
              {groupedDeals.map((section) => (
                <React.Fragment key={section.key}>
                  <DateSectionHeader variant="list" label={section.label} count={section.items.length} compact />
                  {section.items.map((deal) => (
                    <div
                      key={deal.id}
                      className={`admin-list-cols-deals admin-list-magnify border-b border-white/[0.06] hover:bg-white/[0.03] cursor-pointer ${listLoupe.classFor(dealLoupeIndexes.get(deal))}`}
                      onMouseEnter={() => listLoupe.focus(dealLoupeIndexes.get(deal))}
                      onClick={() => navigate(`/admin/deals/${deal.id}`)}
                    >
                      <div className="px-4 py-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-['Manrope'] text-sm text-white font-medium">{deal.customer_name || '-'}</span>
                            {deal.payment_type === 'cash' && (
                              <span className="px-1.5 py-0.5 text-[10px] font-['Manrope'] font-semibold uppercase tracking-wide bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                                Cash
                              </span>
                            )}
                            {deal.payment_type !== 'cash' && (deal.is_financing || deal.payment_type === 'financing') && (
                              <span className="px-1.5 py-0.5 text-[10px] font-['Manrope'] font-semibold uppercase tracking-wide bg-blue-500/15 text-blue-300 border border-blue-500/30">
                                Financing
                              </span>
                            )}
                            {(deal.is_trade_in || deal.trade_in_id) && (
                              <span className="px-1.5 py-0.5 text-[10px] font-['Manrope'] font-semibold uppercase tracking-wide bg-orange-500/15 text-orange-300 border border-orange-500/30">
                                Trade-in
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="px-4 py-3 font-['Manrope'] text-sm text-white/80">
                          {deal.vehicle_name
                            || [deal.vehicle_year, deal.vehicle_make, deal.vehicle_model, deal.vehicle_trim].filter(Boolean).join(' ')
                            || '—'}
                        </div>
                        <div className="px-4 py-3">{getStatusBadge(deal.status)}</div>
                        <div className="px-4 py-3 font-['Manrope'] text-sm text-white">
                          {deal.sale_price ? `$${deal.sale_price.toLocaleString()}` : '-'}
                        </div>
                        <div className="px-4 py-3 font-['Manrope'] text-xs text-white/40">{formatDate(deal.created_at)}</div>
                        <div className="admin-list-actions px-4 py-3">
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/admin/deals/${deal.id}`); }}
                            className="p-1.5 hover:bg-white/10 transition-colors"
                            data-testid={`view-deal-${deal.id?.slice(0, 8)}`}
                          >
                            <Eye className="w-4 h-4 text-blue-400" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </React.Fragment>
                ))}
              </div>
          </div>
          </>
        )}
      </div>

      {/* Create Deal Modal */}
      {showCreateModal && (
        <CreateDealModal
          token={token}
          onClose={() => setShowCreateModal(false)}
          onSuccess={(dealId) => {
            setShowCreateModal(false);
            fetchDeals();
            toast.success('Deal created successfully');
            navigate(`/admin/deals/${dealId}`);
          }}
        />
      )}

      {/* Convert Lead Modal */}
      {showConvertModal && (
        <ConvertLeadModal
          token={token}
          onClose={() => setShowConvertModal(false)}
          onSuccess={(dealId) => {
            setShowConvertModal(false);
            fetchDeals();
            toast.success('Deal created successfully');
            navigate(`/admin/deals/${dealId}`);
          }}
        />
      )}

      {/* Merge Modal */}
      {showMergeModal && (
        <MergeDealsModal
          token={token}
          onClose={() => setShowMergeModal(false)}
          onSuccess={(dealId) => {
            setShowMergeModal(false);
            fetchDeals();
            toast.success('Leads merged into deal successfully');
            navigate(`/admin/deals/${dealId}`);
          }}
        />
      )}
    </div>
  );
}

const MERGE_OTHER_LEAD_TYPES = [
  { value: 'financing', label: getLeadTypeLabel('financing') },
  { value: 'contact', label: getLeadTypeLabel('contact') },
  { value: 'vehicle_inquiry', label: getLeadTypeLabel('vehicle_inquiry') },
  { value: 'prequalification', label: getLeadTypeLabel('prequalification') },
];

/* ===== MERGE DEALS MODAL ===== */
function MergeDealsModal({ token, onClose, onSuccess }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [leads, setLeads] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [picker, setPicker] = useState(null);
  const [form, setForm] = useState({
    trade_in_id: '', other_lead_type: 'financing', other_lead_id: '', vehicle_id: '',
    payment_type: 'financing', sale_price: '', down_payment: '', trade_in_value: '',
    trade_in_condition: '', trade_in_notes: '',
  });

  useEffect(() => {
    Promise.all([
      axios.get(`${BACKEND_URL}/api/admin/leads`, { headers }),
      axios.get(`${BACKEND_URL}/api/vehicles`),
    ]).then(([leadsRes, vehiclesRes]) => {
      setLeads(leadsRes.data);
      setVehicles(vehiclesRes.data);
    }).catch(() => toast.error('Failed to load data'))
      .finally(() => setLoading(false));
  }, [headers]);

  const handleOtherLeadTypeChange = (other_lead_type) => {
    setForm((prev) => ({ ...prev, other_lead_type, other_lead_id: '' }));
  };

  const handleVehicleChange = (vehicleId) => {
    const vehicle = vehicles.find((v) => v.id === vehicleId);
    setForm((prev) => ({
      ...prev,
      vehicle_id: vehicleId,
      sale_price: vehicle?.price ? String(vehicle.price) : prev.sale_price,
      down_payment: prev.payment_type === 'cash' && vehicle?.price ? String(vehicle.price) : prev.down_payment,
    }));
  };

  const handlePaymentTypeChange = (payment_type) => {
    setForm((prev) => ({
      ...prev,
      payment_type,
      down_payment: payment_type === 'cash' && prev.sale_price ? prev.sale_price : prev.down_payment,
    }));
  };

  const handleSalePriceChange = (sale_price) => {
    setForm((prev) => ({
      ...prev,
      sale_price,
      down_payment: prev.payment_type === 'cash' ? sale_price : prev.down_payment,
    }));
  };

  const selectedTradeIn = leads.find((item) => item.id === form.trade_in_id);
  const selectedOther = leads.find((item) => item.id === form.other_lead_id);
  const otherTypeLabel = MERGE_OTHER_LEAD_TYPES.find((t) => t.value === form.other_lead_type)?.label || 'Other';

  const handlePickLead = (lead) => {
    if (picker === 'trade_in') {
      setForm((prev) => ({ ...prev, trade_in_id: lead.id }));
    } else if (picker === 'other') {
      setForm((prev) => ({ ...prev, other_lead_id: lead.id }));
    }
    setPicker(null);
  };

  const handleSubmit = async () => {
    if (!form.trade_in_id || !form.other_lead_id || !form.vehicle_id) {
      toast.error('Trade-in lead, other lead, and vehicle are required');
      return;
    }
    setSubmitting(true);
    try {
      const params = {
        trade_in_id: form.trade_in_id,
        other_lead_id: form.other_lead_id,
        other_lead_type: form.other_lead_type,
        vehicle_id: form.vehicle_id,
        payment_type: form.payment_type,
        ...(form.sale_price && { sale_price: parseInt(form.sale_price, 10) }),
        ...(form.payment_type === 'financing' && form.down_payment && { down_payment: parseInt(form.down_payment, 10) }),
        ...(form.trade_in_value && { trade_in_value: parseInt(form.trade_in_value, 10) }),
        ...(form.trade_in_condition && { trade_in_condition: form.trade_in_condition }),
        ...(form.trade_in_notes && { trade_in_notes: form.trade_in_notes }),
      };
      const res = await axios.post(`${BACKEND_URL}/api/admin/deals/merge-trade-in-lead`, null, { params, headers });
      onSuccess(res.data.deal_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to merge leads');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <div className={adminOverlay('z-[60]')} data-testid="merge-deals-modal">
      <div className={adminDialogPanel('max-w-lg')}>
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h2 className="text-lg font-['Unbounded'] font-bold text-white">Merge Trade-in Lead</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white text-xl">&times;</button>
        </div>
        <div className="p-5 space-y-4">
          {loading ? (
            <p className="text-white/40 font-['Manrope'] text-sm text-center py-4">Loading...</p>
          ) : (
            <>
              <div>
                <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Trade-in Lead *</label>
                <button
                  type="button"
                  onClick={() => setPicker('trade_in')}
                  data-testid="merge-tradein-select"
                  className="w-full flex items-center justify-between gap-3 bg-white/5 border border-white/10 text-sm px-3 py-2.5 font-['Manrope'] hover:bg-white/10 focus:outline-none focus:border-blue-500/50 text-left"
                >
                  {selectedTradeIn ? (
                    <span className="min-w-0 truncate text-white">{selectedTradeIn.name || 'Selected trade-in lead'}</span>
                  ) : (
                    <span className="text-white/45">Choose trade-in Lead...</span>
                  )}
                  <ChevronDown className="w-4 h-4 text-white/35 shrink-0" />
                </button>
              </div>
              <div>
                <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Other Lead Type *</label>
                <select
                  value={form.other_lead_type}
                  onChange={(e) => handleOtherLeadTypeChange(e.target.value)}
                  data-testid="merge-other-lead-type-select"
                  className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 [&>option]:bg-gray-900"
                >
                  {MERGE_OTHER_LEAD_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Other Lead *</label>
                <button
                  type="button"
                  onClick={() => setPicker('other')}
                  data-testid="merge-other-lead-select"
                  className="w-full flex items-center justify-between gap-3 bg-white/5 border border-white/10 text-sm px-3 py-2.5 font-['Manrope'] hover:bg-white/10 focus:outline-none focus:border-blue-500/50 text-left"
                >
                  {selectedOther ? (
                    <span className="min-w-0 truncate text-white">
                      {selectedOther.name || 'Selected lead'} — {CONVERT_LEAD_TYPE_LABELS[selectedOther.type] || selectedOther.type}
                    </span>
                  ) : (
                    <span className="text-white/45">Choose Other Lead...</span>
                  )}
                  <ChevronDown className="w-4 h-4 text-white/35 shrink-0" />
                </button>
              </div>
              <div>
                <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Purchase Type *</label>
                <select
                  value={form.payment_type}
                  onChange={(e) => handlePaymentTypeChange(e.target.value)}
                  data-testid="merge-payment-type"
                  className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 [&>option]:bg-gray-900"
                >
                  {PAYMENT_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Vehicle *</label>
                <select
                  value={form.vehicle_id}
                  onChange={(e) => handleVehicleChange(e.target.value)}
                  data-testid="merge-vehicle-select"
                  className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 [&>option]:bg-gray-900"
                >
                  <option value="">Choose a vehicle...</option>
                  {vehicles.map((v) => (
                    <option key={v.id} value={v.id}>{v.year} {v.make} {v.model} - ${v.price?.toLocaleString()}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Sale Price</label>
                  <input type="number" value={form.sale_price} onChange={(e) => handleSalePriceChange(e.target.value)} placeholder="28000" className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
                </div>
                {form.payment_type === 'financing' ? (
                  <div>
                    <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Down Payment</label>
                    <input type="number" value={form.down_payment} onChange={(e) => setForm({ ...form, down_payment: e.target.value })} placeholder="5000" className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Cash Amount</label>
                    <input type="number" value={form.sale_price} readOnly className="w-full bg-white/[0.03] border border-white/10 text-white/50 text-sm px-3 py-2.5 font-['Manrope'] cursor-not-allowed" />
                  </div>
                )}
                <div>
                  <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Trade-in Value</label>
                  <input type="number" value={form.trade_in_value} onChange={(e) => setForm({ ...form, trade_in_value: e.target.value })} placeholder="3000" className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Trade-in Condition</label>
                  <select value={form.trade_in_condition} onChange={(e) => setForm({ ...form, trade_in_condition: e.target.value })} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 [&>option]:bg-gray-900">
                    <option value="">Select...</option>
                    <option value="Excellent">Excellent</option>
                    <option value="Good">Good</option>
                    <option value="Fair">Fair</option>
                    <option value="Poor">Poor</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Trade-in Notes</label>
                  <input type="text" value={form.trade_in_notes} onChange={(e) => setForm({ ...form, trade_in_notes: e.target.value })} placeholder="Notes..." className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50" />
                </div>
              </div>
              {form.payment_type === 'cash' && (
                <p className="text-xs font-['Manrope'] text-emerald-400/80">
                  Cash deals start at Ready to Close — no lender approval required.
                </p>
              )}
            </>
          )}
        </div>
        <div className="flex justify-end gap-3 p-5 border-t border-white/10">
          <button onClick={onClose} className="px-4 py-2 border border-white/10 text-white/60 text-sm font-['Manrope'] hover:bg-white/5 transition-colors">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={submitting || loading}
            data-testid="merge-submit-btn"
            className="px-5 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold transition-colors disabled:opacity-50"
          >
            {submitting ? 'Merging...' : 'Merge into Deal'}
          </button>
        </div>
      </div>
    </div>
    {picker && (
      <ConvertLeadPicker
        leads={leads}
        selectedId={picker === 'trade_in' ? form.trade_in_id : form.other_lead_id}
        onSelect={handlePickLead}
        onClose={() => setPicker(null)}
        showTypeFilter={false}
        allowedTypes={picker === 'trade_in' ? ['trade_in'] : [form.other_lead_type]}
        title={picker === 'trade_in' ? 'Choose a Trade-in Lead' : `Choose ${otherTypeLabel} Lead`}
        testId={picker === 'trade_in' ? 'merge-tradein-picker' : 'merge-other-lead-picker'}
      />
    )}
    </>
  );
}
